/**
 * A deploy removes the previous build's hashed chunks, and the bucket answers
 * a missing asset with `index.html` and a 200. A tab whose `index.html`
 * predates the deploy then fails every lazy `import()` it still has to make —
 * the locale chunks at boot, `UpsellFlow`, `LoginModal` — with `'text/html' is
 * not a valid JavaScript MIME type`, or WebKit's `Importing a module script
 * failed` (SHO-839). Nothing recovers on its own: the user sits on a page that
 * can't load its next chunk until they reload by hand.
 *
 * Vite's `__vitePreload` wraps every dynamic import in the build and dispatches
 * `vite:preloadError` on `window` when one fails, so one reload fetches the
 * current `index.html` and its chunks. At most once a minute per tab: the guard
 * lives in `sessionStorage`, which survives the reload, so a stale `index.html`
 * the CDN keeps serving can't loop — the existing fallbacks (English boot, the
 * error screen) take the second failure as before. A window rather than a
 * one-shot lock, so a tab left open across two deploys recovers from both. The
 * error is still thrown to its call site on purpose: the report `initI18n`
 * sends is the only count of how often a deploy strands a tab.
 */
const RELOAD_GUARD_KEY = "stale-chunk-reloaded-at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

export const installStaleChunkReload = (
  reload: () => void = () => window.location.reload(),
): (() => void) => {
  const onPreloadError = () => {
    // A chunk that fails for lack of network is not a stale one: reloading
    // would trade a working page for the browser's offline screen.
    if (!navigator.onLine) return;
    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    reload();
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
};
